import { ApiClient } from '@/shared/api-client';
import { IActivity } from '@/shared/api-client/models/activity';
import { IAgendaItem } from '@/shared/api-client/models/agenda/agenda-item';
import { IHierarchyNode } from '@/shared/api-client/models/hierarchy-node';
import { Component, Prop, Provide, Vue } from 'vue-property-decorator';
import AgendaHierarchyItemComponent from './../agenda-hierarchy-item-component/agenda-hierarchy-item-component.vue';
import ActivityStreamComponent from './../activity-stream-component/activity-stream-component.vue';
import AgendaActiveItemViewerComponent from './../agenda-active-item-viewer-component/agenda-active-item-viewer-component.vue';
import HierarchyComponent from '@/shared/components/hierarchy-component/hierarchy-component.vue';
import { EventBusService } from '@/shared/services/event-bus-service';
import { IActivityEvent } from '../../models/activity-event';
import { IAgendaHierarchyPropertyBag } from '../../models/agenda-hierarchy-property-bag';
import { HierarchyFinder } from '@/shared/helpers/hierarchy-finder';
import { ConsoleFacade, NameOf } from '@/shared/helpers';
import { VoteEnvironmentStore } from '@/public/vote/services/vote-environment-store';
import { IActivitySettings } from '@/shared/models/activity-settings';
import { QueryStringBuilder } from '@/shared/api-client/helpers/query-string-builder';
import { IHubMessage } from '@/shared/api-client/models/activity-event/hub-message';
import { IActivityPublishedStateChange } from '@/shared/api-client/models/activity-event/activity-published-state-change';
import { CandidateMapper } from '@/shared/mappers/candidate-mapper';
import { IWebSocketsConfig } from '@/shared/api-client/models/activity-event/web-sockets-config';
import { WebSocketsHubCreator } from '@/shared/services/web-sockets-hub-creator';
import {IActivityUserStateChange} from '@/shared/api-client/models/activity-event/activity-user-state-change';
import {UIEventHandler} from '@/shared/helpers/ui-event-handler';
import {AttendanceStatus} from '@/shared/api-client/enums/attendance-status';

interface IViewModel {

    isInitialized: boolean;

    activity?: IActivity;

    agenda?: IHierarchyNode<IAgendaItem>[];

    agendaHierarchyPropertyBag: IAgendaHierarchyPropertyBag;

    attendanceStatus: AttendanceStatus;

    failedStateFetchingInRowCount: number;

    initialVotedIn: string[];

    isSpectator: boolean;
}

interface IModals {

    spectatorInvalidVoteErrorModalName: string;
}

interface IEventCurrentValues {

    activityEvent?: IActivityEvent
}

@Component({
    components: {
        HierarchyComponent,
        ActivityStreamComponent,
        AgendaHierarchyItemComponent,
        AgendaActiveItemViewerComponent
    }
})
export default class ActivityMeetingComponent extends Vue {

    @Provide() voteEnvironment = new VoteEnvironmentStore();
    @Provide() activityEventBus = new EventBusService<IActivityEvent>();

    @Prop() public id!: string;
    @Prop({default: undefined}) public currentUserId!: string;
    @Prop() public settings!: IActivitySettings;
    @Prop() public translations!: { [key: string]: string; };
    @Prop() public votingTranslations!: { [key: string]: string; };
    @Prop() public resultTranslations!: { [key: string]: string; };
    @Prop({ default: false}) preventViewChangingLinks!: boolean;
    @Prop({ default: false}) isSpectator!: boolean;
    @Prop({ default: 'NotResponded'}) attendanceStatus!: AttendanceStatus;

    private apiClient: ApiClient = new ApiClient();
    private refreshTimer: any = null;

    public modals: IModals = {
        spectatorInvalidVoteErrorModalName: 'spectatorInvalidVoteErrorModal'
    };

    public eventValues: IEventCurrentValues = {

    };

    public viewModel: IViewModel = {

        isInitialized: false,

        agendaHierarchyPropertyBag: {
            activeAgendaItemId: undefined,
            handledAgendaItemIds: []
        },

        attendanceStatus: 'NotResponded',

        failedStateFetchingInRowCount: 0,

        initialVotedIn: [],

        isSpectator: false
    };

    public get showConnectionErrorMessage(): boolean {

        return this.viewModel.failedStateFetchingInRowCount >= 5;
    }

    public get hasAgenda(): boolean {

        return this.viewModel?.agenda !== undefined && this.viewModel.agenda.length > 0;
    }

    public get agenda(): IHierarchyNode<IAgendaItem>[] | undefined {

        return this.viewModel.agenda;
    }

    public get isInProgress(): boolean {

        return this.eventValues.activityEvent?.activityStatus === 'InProgress';
    }

    public get isNotStarted(): boolean {

        return this.eventValues.activityEvent?.activityStatus === 'NotStarted';
    }

    public get isDuringAttendanceControl(): boolean {

        return this.eventValues.activityEvent?.activityStatus === 'AttendanceControl';
    }

    public get isFinished(): boolean {

        return this.eventValues.activityEvent?.activityStatus === 'Finished'
    }

    public get showAttendanceControlConfirm(): boolean {

        if (this.isDuringAttendanceControl && this.viewModel.attendanceStatus === 'NotResponded') {

            if (!this.attendanceControlVerified && this.settings.attendanceConfirm === 'Manual') {
                return true;
            }
        }

        return false;
    }

    public get showAttendanceControlErrorMessage(): boolean {

        if (!this.isDuringAttendanceControl && !this.isInProgress && !this.isFinished) {

            return false;
        }

        if (this.attendanceControlVerified) {

            return false;
        }

        if (this.attendanceStatus === 'NotResponded' && this.settings.attendanceConfirm === 'Automatic') {
            // Since it will automatically accept attendance, we don't need to show this message
            return false;
        }

        return true;
    }

    public get attendanceControlVerified(): boolean {

        if (this.viewModel.attendanceStatus === 'Attending') {
            return true;
        }

        if (this.settings.requireAcceptedAttendanceControl) {
            // Attendance control is required but not accepted
            return false;
        }

        return true;
    }

    public async created() {

        this.voteEnvironment.preventViewChangingLinks = this.preventViewChangingLinks;

        const eventFunc = async (event?: IActivityEvent) => {

            ConsoleFacade.log(`ActivityComponent: Got activity event`, event);

            if (!this.viewModel.activity) {
                return;
            }

            if (!event) {

                this.$set(this.eventValues, NameOf.property(this.eventValues, x => x.activityEvent), undefined);
                return;
            }

            if (this.eventValues.activityEvent?.agendaItemId !== event.agendaItemId) {

                this.viewModel.agendaHierarchyPropertyBag.activeAgendaItemId = event.agendaItemId;

                if (event.agendaItemId) {

                    this.viewModel.agendaHierarchyPropertyBag.handledAgendaItemIds.push(event.agendaItemId);
                }
            }

            if (this.eventValues.activityEvent?.agendaChecksum !== event.agendaChecksum) {

                if (event?.agenda !== null) {

                    const agenda = event?.agenda as IHierarchyNode<IAgendaItem>[] ?? [];

                    ConsoleFacade.log(`Got new agenda`, agenda);

                    HierarchyFinder.hierarchyWalker<IAgendaItem>(agenda, (node) => {

                        if (node.entity?.publishEventEmittedAt) {
                            this.viewModel.agendaHierarchyPropertyBag.handledAgendaItemIds.push(node.entity.id);
                        }
                    });

                    this.$set(this.viewModel, NameOf.property(this.viewModel, x => x.agenda), agenda);
                } else {
                    this.$set(this.viewModel, NameOf.property(this.viewModel, x => x.agenda), null);
                }
            }

            this.$set(this.eventValues, NameOf.property(this.eventValues, x => x.activityEvent), event);

            await this.checkAutomaticAttendanceConfirm();
        }

        await this.initialize();

        this.activityEventBus.$on('changed', eventFunc);

        await eventFunc(this.activityEventBus.current);
    }

    public async destroyed() {

        if (this.refreshTimer !== null) {

            clearInterval(this.refreshTimer);
        }
    }

    private async checkAutomaticAttendanceConfirm(): Promise<void> {

        if (!this.viewModel.activity?.id) {
            throw new Error(`No activity found`);
        }

        if (!this.isDuringAttendanceControl && !this.isInProgress) {
            ConsoleFacade.log(`Cancelled attendance status loading due to activity status`);
            return;
        }

        if (this.viewModel.attendanceStatus !== 'NotResponded') {
            return;
        }

        if (this.settings.requireAcceptedAttendanceControl && this.settings.attendanceConfirm === 'Automatic') {

            await this.acceptAttendance(undefined);
            return;
        }
    }

    public async acceptAttendance($event?: UIEvent): Promise<void> {

        UIEventHandler.preventDefaults($event);

        if (!this.viewModel.activity?.id) {
            throw new Error(`No activity found`);
        }

        if (!this.isDuringAttendanceControl) {
            throw new Error(`Not during attendance control`);
        }

        const attendanceResult = await this.apiClient.attendance.accept(this.viewModel.activity?.id);

        this.$set(this.viewModel, NameOf.property(this.viewModel, x => x.attendanceStatus), attendanceResult.attendanceStatus);
    }

    private async initialize(): Promise<void> {

        this.viewModel.activity = await this.apiClient.activity.get(this.id);
        this.viewModel.isSpectator = this.isSpectator;
        this.viewModel.attendanceStatus = this.attendanceStatus;

        await this.initializeStream();

        document.addEventListener('visibilitychange', async () => {

            if (document.visibilityState === 'visible') {
                await this.setupInitialState(true);
            }

        }, false);

        this.viewModel.isInitialized = true;

        this.refreshTimer = setInterval(async () => {

            await this.setupInitialState(true);

        }, 1000 * 60 * 5);
    }

    private async initializeStream(): Promise<void> {

        if (!this.viewModel.activity?.id) {

            throw new Error(`No activity set`);
        }

        await this.setupInitialState();
    }

    private async setupInitialState(ignoreWebSocketSetup: boolean = false) {

        const activityId = this.viewModel.activity?.id;

        if (!activityId) {
            throw new Error(`ActivityId is missing`);
        }

        try {
            const initialState = await this.apiClient.activityEvent.getState(activityId);

            if (initialState.state !== undefined) {

                this.activityEventBus.set({
                    agendaItemId: initialState.state.agendaItemId,
                    activityStatus: initialState.state.activityStatus,
                    agenda: initialState.state.agendaState?.agenda ?? [],
                    agendaChecksum: initialState.state.agendaState?.agendaChecksum ?? ``,

                    election: initialState.state.electionState?.election,
                    candidates: CandidateMapper.toCandidate(
                        initialState.state.electionState?.candidates ?? [],
                        (initialState.state?.electionState?.election?.settings?.allowSelfVoting === false)
                            ?  this.currentUserId
                            : undefined
                    ),
                    sumResult: initialState.state.electionState?.sumResult,
                    irvResult: initialState.state.electionState?.irvResult
                });
            }

            if (initialState.votedIn) {

                this.viewModel.initialVotedIn = initialState.votedIn;
            }

            this.$set(this.viewModel, NameOf.property(this.viewModel, x => x.failedStateFetchingInRowCount), 0);

            if (!ignoreWebSocketSetup) {
                this.trySetupWebSockets(initialState.webSocketsConfig);
            }

        } catch(error: any) {

            ConsoleFacade.log(`ActivityComponent: An error occurred`, error);

            this.$set(this.viewModel, NameOf.property(this.viewModel, x => x.failedStateFetchingInRowCount), this.viewModel.failedStateFetchingInRowCount + 1);
        }
    }

    public async handleSpectatorClicked(): Promise<void> {

        $(`#${this.modals.spectatorInvalidVoteErrorModalName}`).modal('show');
    }

    private trySetupWebSockets(webSocketsConfig?: IWebSocketsConfig): void {

        if (!webSocketsConfig) {

            ConsoleFacade.log(`Couldn't set up web socket connection`);
            return;
        }

        ConsoleFacade.log('Setting up web socket connection');

        const url = new QueryStringBuilder()
            .withQueryIfNotEmpty('forward_host', webSocketsConfig.forwardHost)
            .build(webSocketsConfig.apiConnectUrl);

        const webSocketCreator = new WebSocketsHubCreator<IActivityPublishedStateChange>();
        webSocketCreator.connect(url, webSocketsConfig?.userAccessToken);

        webSocketCreator.$on('reconnected', () => {

            ConsoleFacade.log('Reset intial state due to reconnected WS-connection');

            // Reset initial state
            this.setupInitialState(true);
        });

        webSocketCreator.$on('messageReceived', (message: IHubMessage<any>) => {

            if (message.type === 'ActivityUserStateChange') {
                const typedUserStateChange = message.value as IActivityUserStateChange;

                if (typedUserStateChange.activityId === this.id) {

                    if (typedUserStateChange.isSpectator?.defined === true) {
                        this.$set(this.viewModel, NameOf.property(this.viewModel, x => x.isSpectator), typedUserStateChange.isSpectator.value);
                    }

                    if (typedUserStateChange.attendanceStatus?.defined === true) {
                        this.$set(this.viewModel, NameOf.property(this.viewModel, x => x.attendanceStatus), typedUserStateChange.attendanceStatus.value);
                    }
                }

                return;
            }

            if (message.type === 'ActivityPublishedStateChange') {

                message.value = message.value as IActivityPublishedStateChange;

                const current = this.activityEventBus.current;

                if (current) {

                    this.activityEventBus.set({
                        activityStatus: message.value.activityStatus?.defined === true ? message.value.activityStatus.value : current.activityStatus,
                        agendaItemId: message.value.agendaItemId?.defined ? message.value.agendaItemId.value : current.agendaItemId,
                        agendaChecksum: message.value.agendaState?.defined === true ? message.value.agendaState.value?.agendaChecksum : current.agendaChecksum,
                        agenda: message.value.agendaState?.defined === true ? message.value.agendaState.value?.agenda : current.agenda,

                        election: message.value.electionState?.defined === true ? message.value.electionState.value?.election : current.election,
                        candidates: message.value.electionState?.defined === true
                            ? CandidateMapper.toCandidate(message.value.electionState.value?.candidates ?? [],
                                (message.value.electionState.value?.election?.settings?.allowSelfVoting === false)
                                    ?  this.currentUserId
                                    : undefined)
                            : current.candidates,
                        sumResult: message.value.electionState?.defined === true ? message.value.electionState.value?.sumResult : current.sumResult,
                        irvResult: message.value.electionState?.defined === true ? message.value.electionState.value?.irvResult : current.irvResult,
                    });
                }

                return;
            }
        });
    }
}
